/**
 * 青森県の背景ツアー「青森めぐり」。10か所、それぞれに3つの豆知識。
 *
 * 書いてはいけないこと。
 * - 十和田湖の「乙女の像」は現代の彫刻なので画面に入れない。
 * - 龍飛崎の歌謡碑は歌詞が刻まれているので画面に入れない。灯台と階段だけにする。
 * - 弘前城天守は本丸石垣の修理のため曳家で移動しており、いまは本来の石垣の上にない。
 * - 白神山地の世界遺産地域の面積は、林野庁の表記どおり「1万7千ヘクタール」と書く。
 * - 北金ヶ沢のイチョウは「日本一の巨木」ではない。「日本最大のイチョウであり、樹木全体としても
 *   4番目の大きさ」という限定つきの表現をそのまま使う。
 * - 鶴の舞橋は「日本一長い木橋」ではない。「三連太鼓橋では日本一長い木橋」と書く。
 * - 恐山・三内丸山遺跡・小牧野遺跡・亀ヶ岡遺跡は霊場または墓域なので入れていない。
 * - ねぶた・立佞武多の山車は意匠の権利関係が公式に確認できなかったので入れていない。
 * - 仏ヶ浦は恐山を参拝したあとに訪れる巡礼地だったと県が書いている。静かに扱う。
 */

import type { StageTour } from '../types';

export const aomoriSceneryTour: StageTour = {
  scenes: [
    {
      id: 'hirosaki-park',
      name: '弘前公園の桜',
      area: '弘前市',
      image: '/assets/scenery/aomori/scenes/01_hirosaki.webp',
      hotspots: [
        {
          id: 'hirosaki-park-variety',
          x: 0.28,
          y: 0.32,
          title: '52種類・約2600本',
          body: '弘前公園にはソメイヨシノを中心に、枝垂れ桜や八重桜など52種類、約2600本の桜がある。',
          source: 'https://www.city.hirosaki.aomori.jp/city_promotion/topics/2023/10/20231018.html',
          mapQuery: '弘前公園',
        },
        {
          id: 'hirosaki-park-oldest',
          x: 0.6,
          y: 0.55,
          title: '幹周582センチのソメイヨシノ',
          body: '弘前公園には、環境省の巨樹・巨木林データベースに登録されたソメイヨシノのなかで、1本の幹の太さがいちばん大きい木がある。弘前市はこれを「日本一太いソメイヨシノ」と呼んでいる。2023年の計測で幹周は582センチ、前回より45センチ太くなっていた。',
          source: 'https://www.city.hirosaki.aomori.jp/city_promotion/topics/2023/10/20231018.html',
          mapQuery: '弘前公園 日本一太いソメイヨシノ',
        },
        {
          id: 'hirosaki-park-tenshu',
          x: 0.8,
          y: 0.3,
          title: '東北に残るただひとつの天守',
          body: '弘前城天守は文化7年（1810年）に完成した三重三階の独立天守で、江戸時代後期の天守の典型のひとつ。東北地方に残る唯一の遺構として重要文化財に指定されている。いまは本丸石垣の修理のため、本丸の中央へ曳家で移して公開されている。',
          source: 'https://www.city.hirosaki.aomori.jp/gaiyou/bunkazai/kuni/kuni9.html',
          mapQuery: '弘前城天守',
        },
      ],
    },
    {
      id: 'aoike',
      name: '十二湖 青池',
      area: '深浦町',
      image: '/assets/scenery/aomori/scenes/02_aoike.webp',
      hotspots: [
        {
          id: 'aoike-origin',
          x: 0.22,
          y: 0.7,
          title: '地震でできた33の湖沼',
          body: '宝永元年（1704年）4月に羽後・津軽地域を襲ったマグニチュード6.9の大地震で沢がせき止められ、地盤が陥没してできた。実際には33の湖沼があり、「十二湖」の名は展望地から大きな12個が見えることに由来する。',
          source:
            'https://www.rinya.maff.go.jp/tohoku/introduction/gaiyou_kyoku/annai/midokoro/midokoro_jyuniko.html',
          mapQuery: '十二湖',
        },
        {
          id: 'aoike-size',
          x: 0.5,
          y: 0.5,
          title: '周囲125メートル、深さ約9メートル',
          body: '青池は湖の周囲が125メートルしかない小さな池。最大水深は約9メートルあるが、底まで見えるほど透き通っている。',
          source: 'https://www.pref.aomori.lg.jp/soshiki/kankyo/ao-kankyo/Aoike_Top_01.html',
          mapQuery: '青池 十二湖',
        },
        {
          id: 'aoike-why-blue',
          x: 0.75,
          y: 0.75,
          title: '白い岩が光を返す',
          body: '青く見える理由は4つある。水が赤い光を吸収して青い光を散らすこと、湧水源で濁りが極めて薄いこと、湖底が十二湖凝灰岩という白っぽい火山灰の岩ですべての波長を等しく反射すること、そして湖底が比較的深いこと。見頃は4月から6月頃。',
          source: 'https://www.pref.aomori.lg.jp/soshiki/kankyo/ao-kankyo/Aoike_Top_01.html',
          mapQuery: '青池 深浦町',
        },
      ],
    },
    {
      id: 'oirase',
      name: '奥入瀬渓流',
      area: '十和田市',
      image: '/assets/scenery/aomori/scenes/03_oirase.webp',
      hotspots: [
        {
          id: 'oirase-only-river',
          x: 0.3,
          y: 0.6,
          title: '十和田湖から出るただひとつの川',
          body: '奥入瀬渓流は十和田湖から流れ出る唯一の川で、子ノ口から焼山まで約14キロの区間をいう。代表的な見どころに「阿修羅の流れ」と「三乱の流れ」がある。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/towada-hachimantai/course/01/',
          mapQuery: '奥入瀬渓流',
        },
        {
          id: 'oirase-designation',
          x: 0.65,
          y: 0.35,
          title: '特別名勝かつ天然記念物',
          body: '昭和27年3月29日、子ノ口から焼山までの約14キロが国の特別名勝および天然記念物に指定された。',
          source: 'https://www.pref.aomori.lg.jp/soshiki/kyoiku/e-bunka/kinen_meisyo_1_01.html',
          mapQuery: '奥入瀬渓流 子ノ口',
        },
        {
          id: 'oirase-ushape',
          x: 0.5,
          y: 0.8,
          title: 'U字型の渓谷',
          body: '環境省は「延長約14kmにわたるU字型の渓谷」とし、流れる水の清澄さ、変化に富んだ流れ、苔むした岩々、岸辺を覆う巨木林、岩壁にかかる数多くの滝を挙げている。',
          source: 'https://www.env.go.jp/park/towada/guide/view.html',
          mapQuery: '奥入瀬渓流 焼山',
        },
      ],
    },
    {
      id: 'tanesashi',
      name: '種差海岸',
      area: '八戸市',
      image: '/assets/scenery/aomori/scenes/04_tanesashi.webp',
      hotspots: [
        {
          id: 'tanesashi-meisho',
          x: 0.25,
          y: 0.35,
          title: '昭和12年に国の名勝',
          body: '種差海岸は昭和12年に国の名勝に指定された。範囲は蕪島の南から大久喜に至る延長12キロメートルの海岸と、その後背地を含めた約880ヘクタールにおよぶ。',
          source:
            'https://www.city.hachinohe.aomori.jp/soshikikarasagasu/shakaikyoikuka/bunka/2/4939.html',
          mapQuery: '種差海岸',
        },
        {
          id: 'tanesashi-shiba',
          x: 0.55,
          y: 0.7,
          title: '鳴き砂と芝草原',
          body: '環境省は「岩礁、草原、砂浜、松林と変化に富む海岸」と書く。遊歩道が整備され、季節ごとの花、鳴き砂の浜、広大なシバ草原が楽しめる。',
          source: 'https://www.env.go.jp/park/sanriku/guide/view.html',
          mapQuery: '種差海岸 天然芝生地',
        },
        {
          id: 'tanesashi-park',
          x: 0.8,
          y: 0.5,
          title: '三陸復興国立公園',
          body: '昭和30年に陸中海岸国立公園として指定され、平成25年5月24日に三陸復興国立公園へ名称を変えて、青森県八戸市蕪島から階上町小舟渡までを含むように広がった。面積は28,539ヘクタール。',
          source: 'https://www.env.go.jp/park/sanriku/intro/index.html',
          mapQuery: '三陸復興国立公園 種差海岸',
        },
      ],
    },
    {
      id: 'hotokegaura',
      name: '仏ヶ浦',
      area: '佐井村',
      image: '/assets/scenery/aomori/scenes/05_hotokegaura.webp',
      solemn: true,
      hotspots: [
        {
          id: 'hotokegaura-designation',
          x: 0.3,
          y: 0.3,
          title: '高さ6〜90メートルの奇岩',
          body: '青森県の文化財名は「仏宇多（仏ヶ浦）」。昭和16年4月23日に名勝および天然記念物に指定された。高さ6メートルから90メートルにおよぶ奇岩が幾百も並ぶ。',
          source: 'https://www.pref.aomori.lg.jp/soshiki/kyoiku/e-bunka/kinen_meisyo_2_01.html',
          mapQuery: '仏ヶ浦',
        },
        {
          id: 'hotokegaura-names',
          x: 0.6,
          y: 0.55,
          title: '岩につけられた仏の名',
          body: 'およそ2キロメートルにわたって奇岩が続く。如来の首、十三仏、五百羅漢、一ッ仏、親子岩、観音岩、天竜岩、蓮華岩、地蔵岩、極楽岩と名がつく。仏像仏具を想わせることが「仏ヶ浦」という名の由来でもある。',
          source: 'https://www.vill.sai.lg.jp/about-saimura/hotokegaura-scenic-site/',
          mapQuery: '仏ヶ浦 佐井村',
        },
        {
          id: 'hotokegaura-pilgrimage',
          x: 0.5,
          y: 0.82,
          title: '恐山とともに巡拝された浜',
          body: '岩は緑色凝灰岩で、海の浸食と風化を受けて今の姿になった。青森県は、仏ヶ浦を恐山と一体をなすものとし、恐山に参詣した人々がこの海岸を巡拝したといわれていると記している。',
          source: 'https://www.pref.aomori.lg.jp/soshiki/kyoiku/e-bunka/kinen_meisyo_2_01.html',
          mapQuery: '仏ヶ浦 遊覧船',
        },
      ],
    },
    {
      id: 'kenashitai',
      name: '八甲田 毛無岱',
      area: '青森市',
      image: '/assets/scenery/aomori/scenes/06_kenashitai.webp',
      hotspots: [
        {
          id: 'kenashitai-odake',
          x: 0.72,
          y: 0.28,
          title: '最高峰は1585メートルの大岳',
          body: '八甲田の最高峰は標高1,585メートルの大岳。山麓には高層湿原や樹林帯が広がる。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/towada-hachimantai/course/08/',
          mapQuery: '八甲田山 大岳',
        },
        {
          id: 'kenashitai-course',
          x: 0.3,
          y: 0.62,
          title: '8.5キロの周回路',
          body: '上毛無岱と下毛無岱を通る周回路は距離8.5キロメートル、所要4時間25分。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/towada-hachimantai/course/08/',
          mapQuery: '毛無岱',
        },
        {
          id: 'kenashitai-snow',
          x: 0.5,
          y: 0.85,
          title: '5メートルを超える雪',
          body: '冬には5メートルを越える、日本有数の豪雪地帯になる。',
          source: 'https://www.env.go.jp/nature/nationalparks/list/towada-hachimantai/course/08/',
          mapQuery: '八甲田 毛無岱 木道',
        },
      ],
    },
    {
      id: 'shirakami',
      name: '白神山地のブナ林',
      area: '西目屋村',
      image: '/assets/scenery/aomori/scenes/07_shirakami.webp',
      hotspots: [
        {
          id: 'shirakami-area',
          x: 0.25,
          y: 0.35,
          title: '約13万ヘクタールの森',
          body: '白神山地は青森県と秋田県に広がる約13万ヘクタールの森林地帯で、国有林が10万ヘクタール、民有林が3万ヘクタールを占める。',
          source: 'https://www.rinya.maff.go.jp/tohoku/230828.html',
          mapQuery: '白神山地',
        },
        {
          id: 'shirakami-heritage',
          x: 0.58,
          y: 0.55,
          title: '世界遺産地域は1万7千ヘクタール',
          body: '世界遺産地域は全域が国有林で、1万7千ヘクタールを占める。原生的なブナの天然林が、世界自然遺産のおもな登録理由になった。',
          source: 'https://www.rinya.maff.go.jp/tohoku/230828.html',
          mapQuery: '白神山地 世界遺産地域',
        },
        {
          id: 'shirakami-buna',
          x: 0.78,
          y: 0.78,
          title: '遺産地域の外にも原生林',
          body: '原生的なブナの天然林は、世界遺産地域の中だけでなく、その周辺の国有林などにも多く見られる。',
          source: 'https://www.rinya.maff.go.jp/tohoku/230828.html',
          mapQuery: '白神山地 ブナ林',
        },
      ],
    },
    {
      id: 'kitakanegasawa',
      name: '北金ヶ沢のイチョウ',
      area: '深浦町',
      image: '/assets/scenery/aomori/scenes/08_kitakanegasawa.webp',
      hotspots: [
        {
          id: 'kitakanegasawa-designation',
          x: 0.22,
          y: 0.75,
          title: '平成16年に天然記念物',
          body: '深浦町北金ヶ沢字塩見形にあり、平成16年9月30日に天然記念物に指定された。',
          source: 'https://www.pref.aomori.lg.jp/soshiki/kyoiku/e-bunka/kinen_tennen_1_06.html',
          mapQuery: '北金ヶ沢のイチョウ',
        },
        {
          id: 'kitakanegasawa-size',
          x: 0.5,
          y: 0.5,
          title: '幹周22メートル、樹高31メートル',
          body: '目通りの幹周は22メートル、樹高は31メートル。',
          source: 'https://www.pref.aomori.lg.jp/soshiki/kyoiku/e-bunka/kinen_tennen_1_06.html',
          mapQuery: '深浦町 北金ヶ沢',
        },
        {
          id: 'kitakanegasawa-rank',
          x: 0.76,
          y: 0.3,
          title: '日本最大のイチョウとされる',
          body: '青森県は「日本最大のイチョウであり、樹木全体としても4番目の大きさの巨樹である」と記している。',
          source: 'https://www.pref.aomori.lg.jp/soshiki/kyoiku/e-bunka/kinen_tennen_1_06.html',
          mapQuery: '北金ヶ沢 大イチョウ',
        },
      ],
    },
    {
      id: 'tsuru-no-maihashi',
      name: '鶴の舞橋',
      area: '鶴田町',
      image: '/assets/scenery/aomori/scenes/09_tsurunomaihashi.webp',
      hotspots: [
        {
          id: 'tsuru-no-maihashi-length',
          x: 0.3,
          y: 0.55,
          title: '全長300メートル',
          body: '全長300メートル。三連太鼓橋では日本一長い木橋である。',
          source: 'https://www.town.tsuruta.lg.jp/kankou/kankou-kankou/tsurumaihashi.html',
          mapQuery: '鶴の舞橋',
        },
        {
          id: 'tsuru-no-maihashi-hiba',
          x: 0.55,
          y: 0.72,
          title: 'ひばの丸太3千本',
          body: '材は青森県産の「ひば」1等材で、丸太3千本と板材3千枚を使っている。',
          source: 'https://www.town.tsuruta.lg.jp/kankou/kankou-kankou/tsurumaihashi.html',
          mapQuery: '鶴田町 鶴の舞橋',
        },
        {
          id: 'tsuru-no-maihashi-lake',
          x: 0.75,
          y: 0.3,
          title: '津軽富士見湖',
          body: '橋が架かるのは廻堰大溜池（まわりぜきおおためいけ）。別名を津軽富士見湖といい、その名のとおり岩木山が水面に映る。',
          source: 'https://www.town.tsuruta.lg.jp/kankou/kankou-kankou/tsurumaihashi.html',
          mapQuery: '津軽富士見湖',
        },
      ],
    },
    {
      id: 'tappizaki',
      name: '龍飛崎と階段国道',
      area: '外ヶ浜町',
      image: '/assets/scenery/aomori/scenes/10_tappizaki.webp',
      hotspots: [
        {
          id: 'tappizaki-stairs',
          x: 0.32,
          y: 0.7,
          title: '日本で唯一の階段国道',
          body: '国道339号は岬の下から灯台までが362段の階段になっている。日本で唯一の階段国道で、総延長は388.2メートル。',
          source: 'https://www.town.sotogahama.lg.jp/kanko/spot/minmaya.html',
          mapQuery: '階段国道339号',
        },
        {
          id: 'tappizaki-lighthouse',
          x: 0.68,
          y: 0.3,
          title: '日本の灯台50選',
          body: '龍飛埼灯台は日本の灯台50選に選ばれ、津軽海峡を行き交う船の安全を見守っている。',
          source: 'https://www.town.sotogahama.lg.jp/kanko/spot/minmaya.html',
          mapQuery: '龍飛埼灯台',
        },
        {
          id: 'tappizaki-park',
          x: 0.5,
          y: 0.88,
          title: '津軽国定公園',
          body: '津軽国定公園は昭和50年3月31日に指定され、面積は25,966ヘクタール。竜飛崎・権現崎・高野崎などの海岸浸食景観を含み、外ヶ浜町から深浦町までの延長約180キロメートルの海岸部と山岳部、湖沼群からなる。',
          source: 'https://www.pref.aomori.lg.jp/soshiki/kankyo/shizen/kouen_tsugaru.html',
          mapQuery: '龍飛崎',
        },
      ],
    },
  ],
};
